import { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import type { Brand } from '../types';

interface KbEntry {
  id: number;
  brand_id: string | null;
  category: string;
  title: string;
  content: string;
  tags: string | null;
  sort_order: number;
  active: number;
  created_at: string;
  updated_at: string;
}

const CATEGORIES = ['product', 'pricing', 'service', 'comparison', 'general'];

export function KnowledgeBasePage() {
  const [entries, setEntries] = useState<KbEntry[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<KbEntry | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState('product');
  const [formBrandId, setFormBrandId] = useState<string>('');
  const [formTags, setFormTags] = useState('');
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formActive, setFormActive] = useState(true);

  useEffect(() => { fetchBrands(); }, []);
  useEffect(() => { fetchEntries(); }, [brandFilter, categoryFilter]);

  const fetchBrands = async () => {
    try {
      const res = await fetch(getApiUrl('/api/brands'), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBrands(data.brands || []);
      }
    } catch {}
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (brandFilter) params.set('brand', brandFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      params.set('limit', '200');

      const res = await fetch(getApiUrl(`/api/knowledge-base?${params}`), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
      }
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormTitle('');
    setFormContent('');
    setFormCategory('product');
    setFormBrandId('');
    setFormTags('');
    setFormSortOrder(0);
    setFormActive(true);
    setShowModal(true);
  };

  const openEdit = (entry: KbEntry) => {
    setEditing(entry);
    setFormTitle(entry.title);
    setFormContent(entry.content);
    setFormCategory(entry.category);
    setFormBrandId(entry.brand_id || '');
    setFormTags(entry.tags || '');
    setFormSortOrder(entry.sort_order);
    setFormActive(!!entry.active);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formTitle.trim() || !formContent.trim()) return;
    setSaving(true);

    const body = {
      title: formTitle,
      content: formContent,
      category: formCategory,
      brand_id: formBrandId || null,
      tags: formTags || null,
      sort_order: formSortOrder,
      active: formActive,
    };

    try {
      const url = editing
        ? getApiUrl(`/api/knowledge-base/${editing.id}`)
        : getApiUrl('/api/knowledge-base');

      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setShowModal(false);
        fetchEntries();
      } else {
        const data = await res.json();
        alert(`Failed to save: ${data.error}`);
      }
    } catch {
      alert('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this knowledge base entry?')) return;
    try {
      await fetch(getApiUrl(`/api/knowledge-base/${id}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchEntries();
    } catch {
      alert('Failed to delete entry');
    }
  };

  const getBrandName = (brandId: string | null) => {
    if (!brandId) return 'All Brands';
    return brands.find(b => b.id === brandId)?.name || brandId;
  };

  const categoryBadge = (cat: string) => {
    const styles: Record<string, string> = {
      product: 'bg-blue-900/50 text-blue-300 border-blue-700',
      pricing: 'bg-green-900/50 text-green-300 border-green-700',
      service: 'bg-purple-900/50 text-purple-300 border-purple-700',
      comparison: 'bg-yellow-900/50 text-yellow-300 border-yellow-700',
      general: 'bg-gray-900/50 text-gray-300 border-gray-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[cat] || styles.general}`}>
        {cat}
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Knowledge Base</h1>
          <p className="text-dark-text-secondary mt-1">
            Manage product knowledge for the AI Q&A agent
          </p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          + Add Entry
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select value={brandFilter} onChange={e => setBrandFilter(e.target.value)} className="input text-sm">
          <option value="">All Brands</option>
          {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="input text-sm">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Entries Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-dark-text-secondary">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-dark-text-secondary">
            No knowledge base entries yet. Click "+ Add Entry" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-dark-text-secondary uppercase">Title</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-dark-text-secondary uppercase">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-dark-text-secondary uppercase">Brand</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-dark-text-secondary uppercase">Active</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-dark-text-secondary uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id} className="border-b border-dark-border hover:bg-slate-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <button onClick={() => openEdit(entry)} className="text-sm text-white font-medium hover:text-blue-400 text-left">
                        {entry.title}
                      </button>
                      <p className="text-xs text-dark-text-secondary mt-0.5 line-clamp-1">{entry.content.substring(0, 80)}...</p>
                    </td>
                    <td className="px-4 py-3">{categoryBadge(entry.category)}</td>
                    <td className="px-4 py-3 text-sm text-dark-text">{getBrandName(entry.brand_id)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${entry.active ? 'text-green-400' : 'text-gray-500'}`}>
                        {entry.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowModal(false)} />
          <div className="relative bg-dark-surface border border-dark-border rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-dark-border">
              <h2 className="text-lg font-bold text-white">
                {editing ? 'Edit Entry' : 'New Knowledge Base Entry'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-dark-text-secondary hover:text-white p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-1">Title</label>
                <input
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  className="input w-full"
                  placeholder="e.g. A4 Professional Copier Specs"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-1">Category</label>
                  <select value={formCategory} onChange={e => setFormCategory(e.target.value)} className="input w-full">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-1">Brand</label>
                  <select value={formBrandId} onChange={e => setFormBrandId(e.target.value)} className="input w-full">
                    <option value="">All Brands (shared)</option>
                    {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text mb-1">
                  Content <span className="text-dark-text-secondary font-normal">(what the AI will see)</span>
                </label>
                <textarea
                  value={formContent}
                  onChange={e => setFormContent(e.target.value)}
                  className="input w-full h-40 resize-y"
                  placeholder="Write detailed product information, specs, selling points, or FAQ answers that the AI agent will use to answer customer questions..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-1">Tags</label>
                  <input
                    value={formTags}
                    onChange={e => setFormTags(e.target.value)}
                    className="input w-full"
                    placeholder="a4, professional, mid volume"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formSortOrder}
                    onChange={e => setFormSortOrder(parseInt(e.target.value) || 0)}
                    className="input w-full"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFormActive(!formActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formActive ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formActive ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-sm text-dark-text">Active</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-dark-border">
              <button onClick={() => setShowModal(false)} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formTitle.trim() || !formContent.trim()}
                className="btn btn-primary"
              >
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
